import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, ChevronRight, BookOpen, ArrowRight } from 'lucide-react';
import { fetchConceptsPlus1 } from '../services/conceptPlus1Service';
import { getMasteryForConcepts, getMasteryStatsForConcepts } from '../services/masteryService';
import { getStoredSession } from '../hooks/useAuth';
import type { ConceptPlus1 } from '../types';
import { Card } from '@branon-shared/ui/Card';
import { EmptyState } from '@branon-shared/ui/EmptyState';

const MASTERY_RING: Record<string, { pct: number; color: string }> = {
  'Strong':        { pct: 1.0,  color: '#22c55e' },
  'Building':      { pct: 0.6,  color: '#f59e0b' },
  'Needs revisit': { pct: 0.3,  color: '#fb7185' },
};

function MasteryRing({ label }: { label?: string }) {
  const r = 10, cx = 14, cy = 14, sw = 3;
  const circ = 2 * Math.PI * r;
  const ring = label ? MASTERY_RING[label] : null;
  const dash = ring ? ring.pct * circ : 0;
  return (
    <svg width={28} height={28} viewBox="0 0 28 28" className="flex-shrink-0">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth={sw} />
      {dash > 0 && (
        <circle
          cx={cx} cy={cy} r={r} fill="none"
          stroke={ring!.color} strokeWidth={sw}
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
    </svg>
  );
}

function formatLastSeen(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}


export default function ConceptList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allConcepts, setAllConcepts] = useState<ConceptPlus1[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState<number | null>(
    searchParams.get('subject') ? Number(searchParams.get('subject')) : null
  );

  const [readinessMap, setReadinessMap] = useState<Record<number, string>>({});
  const [masteryStats, setMasteryStats] = useState<Record<number, { count: number; lastDate: string | null }>>({});

  // Fetch all concepts once
  useEffect(() => {
    async function loadConcepts() {
      setIsLoading(true);
      const data = await fetchConceptsPlus1();
      setAllConcepts(data);
      setIsLoading(false);

      // Load mastery labels for readiness badges (non-blocking)
      const session = getStoredSession();
      if (session?.student_id && data.length > 0) {
        const ids = data.map(c => c.id);
        getMasteryForConcepts(session.student_id, ids).then(setReadinessMap);
        getMasteryStatsForConcepts(session.student_id, ids).then(setMasteryStats);
      }
    }
    loadConcepts();
  }, []);

  // Derive unique subjects from concepts data
  const subjects = useMemo(() => {
    const subjectMap = new Map<number, string>();
    allConcepts.filter(c => c.subject_name !== 'English').forEach(c => {
      if (c.subject_id && c.subject_name) {
        subjectMap.set(c.subject_id, c.subject_name);
      }
    });
    return Array.from(subjectMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allConcepts]);

  // Filter concepts by selected subject (client-side)
  const concepts = useMemo(() => {
    const visible = allConcepts.filter(c => c.subject_name !== 'English');
    if (!selectedSubject) return visible;
    return visible.filter(c => c.subject_id === selectedSubject);
  }, [allConcepts, selectedSubject]);

  // Filter concepts by search query
  const filteredConcepts = concepts.filter(concept =>
    concept.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Concepts where mastery has decayed — shown as prominent nudge when no filter active
  const reviewDueConcepts = useMemo(() => {
    if (Object.keys(readinessMap).length === 0) return [];
    return allConcepts.filter(c => readinessMap[c.id] === 'Needs revisit' && c.subject_name !== 'English');
  }, [allConcepts, readinessMap]);

  const handleSubjectChange = (subjectId: number | null) => {
    setSelectedSubject(subjectId);
    if (subjectId) {
      setSearchParams({ subject: String(subjectId) });
    } else {
      setSearchParams({});
    }
  };

  return (
    <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-quicksand text-xl sm:text-2xl font-bold text-ink">Concepts</h1>
        <p className="text-gray-600 text-sm mt-1">
          {selectedSubject ? subjects.find(s => s[0] === selectedSubject)?.[1] : 'All Subjects'}
        </p>
      </div>

      {/* Review before exam — only when mastery loaded, no active filter/search */}
      {reviewDueConcepts.length > 0 && !searchQuery && selectedSubject === null && (
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm font-bold text-critical">Review before exam</span>
            <span className="px-2 py-0.5 rounded-full bg-critical-soft text-critical text-xs font-bold">
              {reviewDueConcepts.length}
            </span>
            <span className="text-xs text-gray-500">These topics need attention</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {reviewDueConcepts.map((c) => (
              <Link
                key={c.id}
                to={`/concept/${c.id}`}
                className="group flex items-center gap-3 rounded-card border-2 border-critical bg-critical-soft px-4 py-3 hover:opacity-90 transition-opacity"
              >
                <span className="w-2 h-2 rounded-full bg-critical flex-shrink-0" />
                <span className="flex-1 font-semibold text-sm text-ink truncate">
                  {c.name}
                </span>
                {c.subject_name && (
                  <span className="text-[10px] text-gray-500 font-medium flex-shrink-0">{c.subject_name}</span>
                )}
                <ArrowRight className="w-4 h-4 text-critical group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search concepts..."
            className="w-full pl-10 pr-4 py-3 rounded-card border-2 border-gray-200 focus:outline-none focus:border-coral bg-white text-sm sm:text-base"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400 hidden sm:block" />
          <select
            value={selectedSubject || ''}
            onChange={(e) => handleSubjectChange(e.target.value ? Number(e.target.value) : null)}
            className="w-full sm:w-auto px-4 py-3 rounded-card border-2 border-gray-200 focus:outline-none focus:border-coral bg-white text-sm sm:text-base"
          >
            <option value="">All Subjects</option>
            {subjects.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Concepts Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="rounded-card border-2 border-gray-100 p-4 sm:p-5 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-100 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : filteredConcepts.length === 0 ? (
        <Card>
          <EmptyState icon={BookOpen} title="No concepts found" subtitle={
            searchQuery.toLowerCase().includes('commerce')
              ? 'At Grades 11-12, Commerce is covered under Business Studies and Accountancy — try searching or filtering by those instead.'
              : 'Try a different search or filter'
          } />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {filteredConcepts.map((concept) => {
            const stats = masteryStats[concept.id];
            const masteryLabel = readinessMap[concept.id];
            return (
              <Link key={concept.id} to={`/concept/${concept.id}`} className="block">
                <Card className="h-full hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-quicksand font-bold text-ink text-sm sm:text-base pr-2">
                      {concept.name}
                    </h3>
                    <MasteryRing label={masteryLabel} />
                  </div>
                  {stats && (
                    <p className="text-[10px] text-gray-400 mb-1">
                      {stats.count} interaction{stats.count !== 1 ? 's' : ''}
                      {stats.lastDate ? ` · Last: ${formatLastSeen(stats.lastDate)}` : ''}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs sm:text-sm font-medium text-coral">
                      {concept.subject_name || 'General'}
                    </p>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
